// The app's appearance. A person chooses light, dark or system; `system`
// follows the device's prefers-color-scheme. localStorage `poneva.theme` is the
// pre-paint cache of that choice on this device. The choice is also a server
// setting (src/settings.mjs), so it follows the person to every device:
// `adopt` applies a server value without saving it, and App's chooseTheme
// applies and saves (PUT /api/v1/settings).
import { useCallback, useEffect, useState } from 'react';
import { THEME_CHOICES, type ThemeChoice } from '@/settings';
import {
  applyThemeToDocument,
  darkQuery,
  loadThemeChoice,
  persistThemeChoice,
  resolveTheme,
  systemPrefersDark,
} from '@/lib/theme-core';

// The decision itself lives in theme-core, which ui/boot.ts also runs before
// the first paint; this module adds the React hooks.
export {
  applyThemeToDocument,
  loadThemeChoice,
  resolveTheme,
  systemPrefersDark,
  THEME_KEY,
  type Theme,
} from '@/lib/theme-core';

/** Whether the device prefers dark, kept current by a matchMedia listener. */
export function useSystemDark(): boolean {
  const [dark, setDark] = useState(systemPrefersDark);
  useEffect(() => {
    const query = darkQuery();
    if (!query) return;
    const update = () => setDark(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  return dark;
}

/**
 * The appearance of this app instance: the chosen value, what it resolves to
 * now, and `adopt` (apply a value, e.g. from the server, without saving it;
 * null keeps the current choice).
 */
export function useThemeChoice() {
  const [choice, setChoice] = useState<ThemeChoice>(loadThemeChoice);
  const systemDark = useSystemDark();
  const theme = resolveTheme(choice, systemDark);
  useEffect(() => {
    persistThemeChoice(choice);
    applyThemeToDocument(theme);
  }, [choice, theme]);
  const adopt = useCallback((next: ThemeChoice | null | undefined) => {
    if (next && THEME_CHOICES.includes(next)) setChoice(next);
  }, []);
  return { choice, theme, adopt };
}
